import { ContextHelpButton } from "@/components/registrant/help/context-help-button";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FileDropzone } from "@/components/ui/file-dropzone";
import { Label } from "@/components/ui/label";
import { UserType } from "@/constants";
import type {
  LicenseCommentResponseSchema,
  LicenseCommentsResponseSchema,
  NonRegistrantLicenseCommentResponseSchema,
  NonRegistrantLicenseCommentsResponseSchema,
} from "@/hera-api/client/types.gen";
import type { HelpPortalId } from "@/lib/help-portal";
import { cn, formatDateTime, mapUserTypeToUIString } from "@/lib/utils";
import { Eye, Trash2 } from "lucide-react";
import { useState } from "react";

function helpPortalForUserType(userType: UserType): HelpPortalId {
  if (userType === UserType.GOVERNMENT) return "government";
  if (userType === UserType.OPERATOR) return "operator";
  if (userType === UserType.OMT_AMS) return "omt-ams";
  return "registrant";
}
import { Avatar, AvatarFallback } from "../../ui/avatar";
import { Separator } from "../../ui/separator";
import { Textarea } from "../../ui/textarea";
import {
  type CommentAttachmentPreview,
  CommentAttachmentsGallery,
} from "./comment-attachments-gallery";

export type CommentSubmitInput = {
  comment: string;
  notifyRegistry: boolean;
  markIssueResolved: boolean;
  files: File[];
};

export type FlagComplianceIssueInput = {
  comment: string;
  files: File[];
};

type CommentAuthor = {
  user_type: string;
  name?: string | null;
  email?: string | null;
  role_label?: string | null;
};

type CommentRow = (
  | LicenseCommentResponseSchema
  | NonRegistrantLicenseCommentResponseSchema
) & {
  notify_registry?: boolean;
  registry_resolved_at?: string | null;
  attachments?: CommentAttachmentPreview[];
  author: CommentAuthor;
};

type ComplianceIssueSummary = {
  id: string;
  status: string;
  raised_by_user_type: string;
  raised_by_name?: string | null;
  role_label?: string | null;
};

interface CommentsCardContentProps {
  commentsData:
    | NonRegistrantLicenseCommentsResponseSchema
    | LicenseCommentsResponseSchema;
  onCommentAdded?: (
    comment: string,
    isPublic: boolean,
    files?: File[],
  ) => Promise<void>;
  onRegistrantComment?: (input: CommentSubmitInput) => Promise<void>;
  onFlagComplianceIssue?: (input: FlagComplianceIssueInput) => Promise<void>;
  onClearRegistryNotification?: (commentId: string) => Promise<void>;
  onClearComplianceIssue?: () => Promise<void>;
  onCommentDeleted?: (commentId: string) => void;
  userType: UserType;
}

function issueStatusLabel(status: string): string {
  if (status === "PENDING_CHECK") return "Ready for Check";
  if (status === "CLEARED") return "Cleared";
  return "Compliance Issue";
}

export function CommentsCardContent({
  commentsData,
  userType,
  onCommentAdded,
  onRegistrantComment,
  onFlagComplianceIssue,
  onClearRegistryNotification,
  onClearComplianceIssue,
  onCommentDeleted,
}: CommentsCardContentProps) {
  const [comment, setComment] = useState("");
  const [notifyRegistry, setNotifyRegistry] = useState(false);
  const [markIssueResolved, setMarkIssueResolved] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [dialogMode, setDialogMode] = useState<
    "comment" | "resolve" | "compliance" | null
  >(null);
  const [isSaving, setIsSaving] = useState(false);

  const issue = (
    commentsData as { compliance_issue?: ComplianceIssueSummary | null }
  ).compliance_issue;
  const issueOpen = issue?.status === "OPEN";
  const issueActive =
    issue?.status === "OPEN" || issue?.status === "PENDING_CHECK";
  const canManageComments =
    userType === UserType.OPERATOR || userType === UserType.GOVERNMENT;
  const isRegistrant = userType === UserType.REGISTRANT;
  const canClearIssue =
    issueActive && issue != null && issue.raised_by_user_type === userType;

  const resetDraft = () => {
    setComment("");
    setNotifyRegistry(false);
    setMarkIssueResolved(false);
    setFiles([]);
  };

  const closeDialog = () => {
    setDialogMode(null);
    resetDraft();
  };

  const openDialog = (mode: "comment" | "resolve" | "compliance") => {
    resetDraft();
    if (mode === "resolve") setMarkIssueResolved(true);
    setDialogMode(mode);
  };

  const handleAddComment = async (shareWithRegistrant = false) => {
    if (!comment.trim()) return;
    setIsSaving(true);
    try {
      if (isRegistrant && onRegistrantComment) {
        await onRegistrantComment({
          comment,
          notifyRegistry,
          markIssueResolved,
          files,
        });
      } else if (onCommentAdded) {
        await onCommentAdded(comment, shareWithRegistrant, files);
      }
      closeDialog();
    } catch (error) {
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleFlag = async () => {
    if (!onFlagComplianceIssue || !comment.trim()) return;
    setIsSaving(true);
    try {
      await onFlagComplianceIssue({ comment, files });
      closeDialog();
    } catch (error) {
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <CardContent>
      {issue ? (
        <div
          className={cn(
            "mb-4 rounded-md border p-3 text-sm",
            issue.status === "OPEN" &&
              "border-destructive/50 bg-destructive/10",
            issue.status === "PENDING_CHECK" &&
              "border-amber-500/50 bg-amber-50",
          )}
        >
          <Badge
            variant={
              issue.status === "OPEN"
                ? "destructive"
                : issue.status === "PENDING_CHECK"
                  ? "warning"
                  : "outline"
            }
          >
            {issueStatusLabel(issue.status)}
          </Badge>
          <p className="mt-2 font-medium">
            Flagged by {issue.raised_by_name || "User"}
            {issue.role_label ? ` (${issue.role_label})` : ""}.
          </p>
          {canClearIssue ? (
            <Button
              className="mt-2"
              variant="outline"
              onClick={() => void onClearComplianceIssue?.()}
            >
              Clear Compliance Issue
            </Button>
          ) : null}
        </div>
      ) : null}

      {(canManageComments || isRegistrant) && (
        <div className="flex flex-col gap-2 pb-4">
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => openDialog("comment")}>
              Add Comment...
            </Button>
            {isRegistrant && issueOpen ? (
              <Button variant="outline" onClick={() => openDialog("resolve")}>
                Report Issue Resolved...
              </Button>
            ) : null}
            {canManageComments ? (
              <Button
                variant="outline"
                onClick={() => openDialog("compliance")}
              >
                Add Compliance Issue...
              </Button>
            ) : null}
          </div>
        </div>
      )}

      {(commentsData.comments as CommentRow[]).map((row) => {
        const authorName = row.author.name?.trim() || "User";
        const role = row.author.role_label?.trim();
        const showEmail = canManageComments && Boolean(row.author.email);
        const unresolvedNotify =
          Boolean(row.notify_registry) && !row.registry_resolved_at;
        return (
          <div
            className={cn(
              "pb-4",
              "is_public" in row && row.is_public && "bg-yellow-100",
            )}
            key={row.id}
          >
            <Separator className="mb-4" />
            <div className="flex space-x-4 px-2">
              <Avatar className="h-10 w-10">
                <AvatarFallback>
                  {authorName.slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 space-y-0">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-xs">
                      <span className="font-bold">{authorName}</span>
                      {role ? ` · ${role}` : ""}
                      {showEmail ? ` ${row.author.email}` : ""}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {mapUserTypeToUIString(row.author.user_type)} •{" "}
                      {formatDateTime(row.created_at)}
                    </p>
                    {"is_public" in row &&
                    row.is_public &&
                    canManageComments ? (
                      <p className="text-xs text-muted-foreground">
                        <strong>
                          <Eye className="inline h-3 w-3" /> Visible to
                          registrant
                        </strong>
                      </p>
                    ) : null}
                    {unresolvedNotify ? (
                      <Badge variant="outline" className="mt-1">
                        Pending Review
                      </Badge>
                    ) : null}
                  </div>
                  <div className="flex items-center gap-1">
                    {canManageComments && unresolvedNotify ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          void onClearRegistryNotification?.(row.id)
                        }
                      >
                        Clear Notification
                      </Button>
                    ) : null}
                    {canManageComments ? (
                      <Button
                        variant="ghost"
                        className="text-destructive hover:text-destructive-foreground hover:bg-destructive"
                        size="icon"
                        onClick={() => onCommentDeleted?.(row.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    ) : null}
                  </div>
                </div>
                <p className="text-sm mt-2">{row.comment}</p>
                <CommentAttachmentsGallery
                  attachments={row.attachments ?? []}
                />
              </div>
            </div>
          </div>
        );
      })}

      <Dialog
        open={dialogMode != null}
        onOpenChange={(open) => {
          if (!open && !isSaving) closeDialog();
        }}
      >
        <DialogContent elevated className="sm:max-w-lg">
          <DialogHeader>
            <div className="flex items-start justify-between gap-2 pr-6">
              <div className="space-y-1.5">
                <DialogTitle>
                  {dialogMode === "compliance"
                    ? "Add Compliance Issue"
                    : dialogMode === "resolve"
                      ? "Report Issue Resolved"
                      : "Add Comment"}
                </DialogTitle>
                <DialogDescription>
                  {dialogMode === "compliance"
                    ? "This issue and its attachments will be visible to the registrant."
                    : dialogMode === "resolve"
                      ? "Saving this comment marks the compliance issue ready for check."
                      : isRegistrant
                        ? "Saved on this registration. Hera Registry can read it. Check Notify Hera Registry if you want it reviewed."
                        : "An internal comment stays with Hera. Posting to the registrant emails them and shows the comment on the registration."}
                </DialogDescription>
              </div>
              {dialogMode === "compliance" || dialogMode === "resolve" ? (
                <ContextHelpButton
                  articleId="compliance-issues"
                  helpPortal={helpPortalForUserType(userType)}
                  iconOnly
                  variant="ghost"
                />
              ) : null}
            </div>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="comment-text">Comment</Label>
              <Textarea
                id="comment-text"
                placeholder={
                  dialogMode === "compliance"
                    ? "Describe the compliance issue..."
                    : "Add a comment..."
                }
                value={comment}
                onChange={(event) => setComment(event.target.value)}
                className="min-h-28"
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <FileDropzone
                density="compact"
                multiple
                accept="image/*,application/pdf"
                label="Drag and drop images or PDFs here"
                chooseFileLabel="Upload files"
                inputId={`${dialogMode ?? "comment"}-attachments`}
                inputAriaLabel="Attach files"
                helperText="You can select multiple files at once."
                disabled={isSaving}
                onFiles={(incoming) =>
                  setFiles((current) => [...current, ...incoming])
                }
              />
              {files.length > 0 ? (
                <ul className="space-y-1">
                  {files.map((file, index) => (
                    <li
                      key={`${file.name}-${file.size}-${index}`}
                      className="flex items-center justify-between gap-2 rounded-md border px-2 py-1"
                    >
                      <span className="truncate text-sm">{file.name}</span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={isSaving}
                        onClick={() =>
                          setFiles((current) =>
                            current.filter(
                              (_, itemIndex) => itemIndex !== index,
                            ),
                          )
                        }
                      >
                        Remove
                      </Button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
            {isRegistrant && dialogMode === "comment" ? (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="notify-hera-registry"
                  checked={notifyRegistry}
                  onCheckedChange={(checked) =>
                    setNotifyRegistry(checked === true)
                  }
                />
                <Label htmlFor="notify-hera-registry" className="text-sm">
                  Notify Hera Registry
                </Label>
              </div>
            ) : null}
          </div>
          <DialogFooter className="flex-row flex-wrap justify-end gap-2 sm:justify-end">
            {canManageComments && dialogMode === "comment" ? (
              <>
                <Button
                  type="button"
                  variant="outline"
                  disabled={isSaving}
                  onClick={closeDialog}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={!comment.trim() || isSaving}
                  onClick={() => void handleAddComment(false)}
                >
                  {isSaving ? "Saving..." : "Add Internal Comment"}
                </Button>
                <Button
                  type="button"
                  disabled={!comment.trim() || isSaving}
                  onClick={() => void handleAddComment(true)}
                >
                  {isSaving ? "Saving..." : "Post Comment to Registrant"}
                </Button>
              </>
            ) : (
              <>
                <Button
                  type="button"
                  variant="outline"
                  disabled={isSaving}
                  onClick={closeDialog}
                >
                  Discard
                </Button>
                <Button
                  type="button"
                  disabled={!comment.trim() || isSaving}
                  onClick={() =>
                    void (dialogMode === "compliance"
                      ? handleFlag()
                      : handleAddComment())
                  }
                >
                  {isSaving ? "Saving..." : "Save"}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </CardContent>
  );
}
